<template>
  <Teleport to="body">
    <div v-if="showModal" class="modal-overlay" @click.self="closeModal">
      <div class="modal-content">
        <h3>Insert Image</h3>
        <div class="modal-tabs">
          <button
            :class="{ active: imageInsertMode === 'url' }"
            @click="imageInsertMode = 'url'"
          >
            From URL
          </button>
          <button
            :class="{ active: imageInsertMode === 'upload' }"
            @click="imageInsertMode = 'upload'"
          >
            Upload
          </button>
        </div>

        <div v-if="imageInsertMode === 'url'" class="modal-body">
          <label for="imageUrlInput">Image URL:</label>
          <input
            type="text"
            id="imageUrlInput"
            v-model="imageUrl"
            placeholder="https://example.com/image.jpg"
          />
        </div>

        <div v-if="imageInsertMode === 'upload'" class="modal-body upload-area">
          <button @click="triggerImageUpload">Choose Image File</button>
          <p v-if="uploadFileName">Selected: {{ uploadFileName }}</p>
          <p v-else>No file chosen</p>
          <input
            type="file"
            ref="imageInput"
            @change="handleImageUpload"
            accept="image/*"
            style="display: none"
          />
        </div>

        <div class="modal-footer">
          <button @click="closeModal" class="btn-cancel">Cancel</button>
          <button
            @click="insertImage"
            :disabled="
              (imageInsertMode === 'url' && !imageUrl) ||
              (imageInsertMode === 'upload' && !uploadedImageSrc)
            "
          >
            Insert
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { defineProps, defineEmits, ref } from "vue";

const props = defineProps({
  editor: {
    type: Object,
    required: true,
  },
  showModal: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["close"]);

const imageInput = ref(null);
const imageInsertMode = ref("url");
const imageUrl = ref("");
const uploadedImageSrc = ref(null);
const uploadFileName = ref("");

const closeModal = () => {
  emit("close");
};

const triggerImageUpload = () => {
  imageInput.value.click();
};

const handleImageUpload = (event) => {
  const file = event.target.files[0];
  if (!file) {
    uploadedImageSrc.value = null;
    uploadFileName.value = "";
    return;
  }
  if (!file.type.startsWith("image/")) {
    alert("Please select an image file.");
    uploadedImageSrc.value = null;
    uploadFileName.value = "";
    return;
  }

  uploadFileName.value = file.name;
  const reader = new FileReader();
  reader.onload = (e) => {
    uploadedImageSrc.value = e.target.result;
  };
  reader.onerror = (error) => {
    console.error("Error reading file:", error);
    alert("Error reading image file.");
    uploadedImageSrc.value = null;
    uploadFileName.value = "";
  };
  reader.readAsDataURL(file);
  event.target.value = "";
};

const insertImage = () => {
  if (imageInsertMode.value === "url" && imageUrl.value) {
    props.editor.chain().focus().setImage({ src: imageUrl.value }).run();
    closeModal();
  } else if (imageInsertMode.value === "upload" && uploadedImageSrc.value) {
    props.editor
      .chain()
      .focus()
      .setImage({ src: uploadedImageSrc.value })
      .run();
    closeModal();
  } else if (imageInsertMode.value === "upload") {
    triggerImageUpload();
  }
};
</script>

<style scoped>
/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background-color: white;
  padding: 25px;
  border-radius: 8px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
  width: 90%;
  max-width: 500px;
}

.modal-content h3 {
  margin-top: 0;
  margin-bottom: 20px;
  font-size: 1.25em;
  color: #343a40;
  text-align: center;
}

.modal-tabs {
  display: flex;
  margin-bottom: 20px;
  border-bottom: 1px solid #dee2e6;
}

.modal-tabs button {
  padding: 10px 15px;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 1em;
  color: #6c757d;
  border-bottom: 3px solid transparent;
  margin-bottom: -1px;
}

.modal-tabs button.active {
  color: var(--primary-blue);
  border-bottom-color: var(--primary-blue);
}

.modal-body {
  margin-bottom: 20px;
}

.modal-body label {
  display: block;
  margin-bottom: 5px;
  font-weight: 500;
  color: #495057;
}

.modal-body input[type="text"] {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  box-sizing: border-box;
}

.modal-body.upload-area button {
  background-color: var(--primary-blue);
  color: white;
  border: none;
  padding: 8px 15px;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s;
  margin-right: 10px;
}
.modal-body.upload-area button:hover {
  background-color: var(--primary-blue-dark);
}
.modal-body.upload-area p {
  display: inline-block;
  color: #6c757d;
  font-size: 0.9em;
  margin: 0;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.modal-footer button {
  padding: 8px 15px;
  border-radius: 4px;
  border: 1px solid #ced4da;
  cursor: pointer;
  font-size: 0.9em;
}

.modal-footer button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.modal-footer button.btn-cancel {
  background-color: #f8f9fa;
  color: #495057;
}
.modal-footer button.btn-cancel:hover {
  background-color: #e9ecef;
}

.modal-footer button:not(.btn-cancel) {
  background-color: var(--primary-blue);
  color: white;
  border-color: var(--primary-blue);
}
.modal-footer button:not(.btn-cancel):not(:disabled):hover {
  background-color: var(--primary-blue-dark);
  border-color: var(--primary-blue-dark);
}
</style>
